/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Where the decision came from.
 */
export const DecidedVia = {
  Slack: "slack",
  Dashboard: "dashboard",
  Api: "api",
} as const;
/**
 * Where the decision came from.
 */
export type DecidedVia = OpenEnum<typeof DecidedVia>;

/**
 * The request's status. An open request past its window is expired.
 */
export const RiskPolicyApprovalStatus = {
  Blocked: "blocked",
  Pending: "pending",
  Approved: "approved",
  Denied: "denied",
  Consumed: "consumed",
  Expired: "expired",
} as const;
/**
 * The request's status. An open request past its window is expired.
 */
export type RiskPolicyApprovalStatus = OpenEnum<
  typeof RiskPolicyApprovalStatus
>;

export type RiskPolicyApproval = {
  /**
   * A shortened preview of the call's arguments: long strings are cut and only the first keys are kept.
   */
  argumentsPreview: { [k: string]: any };
  /**
   * Whether the caller can decide this request now.
   */
  canDecide: boolean;
  /**
   * The chat the call belongs to, when known.
   */
  chatId?: string | undefined;
  /**
   * When a retry used the approval.
   */
  consumedAt?: Date | undefined;
  /**
   * When the call was first held.
   */
  createdAt: Date;
  /**
   * When an approver decided.
   */
  decidedAt?: Date | undefined;
  /**
   * The approver who decided.
   */
  decidedByUrn?: string | undefined;
  /**
   * Where the decision came from.
   */
  decidedVia?: DecidedVia | undefined;
  /**
   * The approver's message for the agent.
   */
  decisionNote?: string | undefined;
  /**
   * When the request stops waiting for a decision, or the approval stops covering a retry.
   */
  expiresAt?: Date | undefined;
  /**
   * What the approver granted, once approved.
   */
  grantedScope?: string | undefined;
  /**
   * The approval request ID.
   */
  id: string;
  /**
   * The require_approval risk policy that held the call.
   */
  policyId: string;
  /**
   * The policy's name.
   */
  policyName: string;
  /**
   * The policy version when the call was held.
   */
  policyVersion: number;
  /**
   * The agent's reason, once it requested approval.
   */
  reason?: string | undefined;
  /**
   * When the agent requested approval.
   */
  requestedAt?: Date | undefined;
  /**
   * What the agent asked to be approved, such as exact_once.
   */
  requestedScope?: string | undefined;
  /**
   * The principal that made the call: agent:<id> or user:<id>.
   */
  requesterUrn: string;
  /**
   * The request's status. An open request past its window is expired.
   */
  status: RiskPolicyApprovalStatus;
  /**
   * The MCP surface that received the call.
   */
  surface: string;
  /**
   * The tool that was called.
   */
  toolName: string;
  /**
   * The hosted toolset that served the call, when known.
   */
  toolsetId?: string | undefined;
  /**
   * Last update timestamp.
   */
  updatedAt: Date;
};

/** @internal */
export const DecidedVia$inboundSchema: z.ZodMiniType<DecidedVia, unknown> =
  openEnums.inboundSchema(DecidedVia);

/** @internal */
export const RiskPolicyApprovalStatus$inboundSchema: z.ZodMiniType<
  RiskPolicyApprovalStatus,
  unknown
> = openEnums.inboundSchema(RiskPolicyApprovalStatus);

/** @internal */
export const RiskPolicyApproval$inboundSchema: z.ZodMiniType<
  RiskPolicyApproval,
  unknown
> = z.pipe(
  z.object({
    arguments_preview: z.record(z.string(), z.any()),
    can_decide: z.boolean(),
    chat_id: z.optional(z.string()),
    consumed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    decided_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    decided_by_urn: z.optional(z.string()),
    decided_via: z.optional(DecidedVia$inboundSchema),
    decision_note: z.optional(z.string()),
    expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    granted_scope: z.optional(z.string()),
    id: z.string(),
    policy_id: z.string(),
    policy_name: z.string(),
    policy_version: z.int(),
    reason: z.optional(z.string()),
    requested_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    requested_scope: z.optional(z.string()),
    requester_urn: z.string(),
    status: RiskPolicyApprovalStatus$inboundSchema,
    surface: z.string(),
    tool_name: z.string(),
    toolset_id: z.optional(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "arguments_preview": "argumentsPreview",
      "can_decide": "canDecide",
      "chat_id": "chatId",
      "consumed_at": "consumedAt",
      "created_at": "createdAt",
      "decided_at": "decidedAt",
      "decided_by_urn": "decidedByUrn",
      "decided_via": "decidedVia",
      "decision_note": "decisionNote",
      "expires_at": "expiresAt",
      "granted_scope": "grantedScope",
      "policy_id": "policyId",
      "policy_name": "policyName",
      "policy_version": "policyVersion",
      "requested_at": "requestedAt",
      "requested_scope": "requestedScope",
      "requester_urn": "requesterUrn",
      "tool_name": "toolName",
      "toolset_id": "toolsetId",
      "updated_at": "updatedAt",
    });
  }),
);

export function riskPolicyApprovalFromJSON(
  jsonString: string,
): SafeParseResult<RiskPolicyApproval, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskPolicyApproval$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskPolicyApproval' from JSON`,
  );
}
